import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  NewspaperIcon,
} from '@heroicons/react/24/outline';
import {
  navigateToInquiry,
  buildPlantingAdvicePath,
  resolveCropDisplayName,
} from '../../../utils/inquiryEntryContext';

const riskColors = {
  高风险: 'bg-red-100 text-red-700 border-red-200',
  中风险: 'bg-orange-100 text-orange-700 border-orange-200',
  低风险: 'bg-green-100 text-green-700 border-green-200',
};

function normalizeRisk(raw) {
  if (!raw) return '低风险';
  const s = String(raw);
  if (s.includes('高')) return '高风险';
  if (s.includes('中')) return '中风险';
  return '低风险';
}

function mapCropItem(c) {
  const advice = c.advice || c.reason || c.description || '';
  return {
    name: c.name || c.cropName || '在种作物',
    advice,
    risk: normalizeRisk(c.riskLevel || c.risk),
    validUntil: c.validUntil || c.timeliness || '',
    sourceTags: Array.isArray(c.sourceTags) ? c.sourceTags : [],
    growthStage: c.growthStage || '',
    landNames: c.landNames || '',
    farmId: c.farmId || c.farm_id || '',
    landId: c.landId || c.land_id || '',
  };
}

/**
 * 植保建议卡片：可进问询或种植建议资讯，并带上 farmId / 默认 landId。
 */
const PlantProtectionAdvice = ({
  welcomeLoading,
  plantProtectionAdvice,
  farmId = '',
  onRefresh,
}) => {
  const navigate = useNavigate();
  const contextFarmId = String(farmId || '').trim();

  const crops = Array.isArray(plantProtectionAdvice?.suitableCrops)
    ? plantProtectionAdvice.suitableCrops.slice(0, 6).map(mapCropItem).filter((c) => c.advice)
    : [];

  const goAdviceList = (crop) => {
    navigate(
      buildPlantingAdvicePath({
        farmId: crop?.farmId || contextFarmId,
        crop: crop?.name,
        cropName: crop?.name,
      }),
      {
        state: crop
          ? { selectedCrop: crop, fromWelcome: true, farmId: crop.farmId || contextFarmId }
          : { fromWelcome: true, farmId: contextFarmId },
      }
    );
  };

  const goInquiry = (crop, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const plantType = resolveCropDisplayName(crop?.name || '');
    const advice = String(crop?.advice || '').trim();
    // 默认不自动绑定地块：只带问题与作物，由问询页用户手动绑定后再传地块给 AI
    navigateToInquiry(navigate, {
      plantType,
      text: advice
        ? `【${plantType || '作物'}】${advice.slice(0, 200)}。请给出可执行的防治与田间管理建议。`
        : undefined,
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden min-w-0">
      <div className="px-4 py-3 flex items-center justify-between gap-2 min-w-0">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 min-w-0">
          <ShieldCheckIcon className="h-4 w-4 text-green-600 shrink-0" aria-hidden />
          <span className="truncate">植保建议</span>
        </h3>
        <div className="flex items-center gap-3 shrink-0">
          {typeof onRefresh === 'function' ? (
            <button
              type="button"
              onClick={onRefresh}
              className="text-sm text-gray-600 hover:text-green-700 transition-colors"
            >
              刷新
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => goAdviceList(null)}
            className="text-sm text-gray-600 hover:text-green-700 transition-colors whitespace-nowrap"
          >
            更多建议
          </button>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-3">
        {welcomeLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin h-5 w-5 border-2 border-green-200 border-t-green-600 rounded-full" />
          </div>
        ) : crops.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center gap-3 py-6 px-2">
            <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" aria-hidden />
            <p className="text-sm font-semibold text-gray-800">还没有在种作物</p>
            <p className="text-sm text-gray-600 leading-relaxed max-w-[280px]">
              添加在种作物后，这里会给出田间建议。
            </p>
            <button
              type="button"
              onClick={() => navigate('/production/crop/plans')}
              className="mt-1 px-4 py-2 text-sm font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700"
            >
              管理在种作物
            </button>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <button
                type="button"
                onClick={() =>
                  navigateToInquiry(navigate, {
                    autoSend: false,
                  })
                }
                className="text-gray-600 hover:text-green-700"
              >
                去植保问询
              </button>
              <button
                type="button"
                onClick={() => goAdviceList(null)}
                className="text-gray-600 hover:text-green-700"
              >
                种植建议资讯
              </button>
            </div>
          </div>
        ) : (
          crops.map((crop, index) => (
            <div
              key={`${crop.name}-${index}`}
              className="w-full flex items-stretch gap-2 p-3 rounded-lg hover:bg-green-50 transition-colors group min-w-0"
            >
              <button
                type="button"
                onClick={() => goAdviceList(crop)}
                className="flex flex-1 items-center gap-3 min-w-0 text-left"
                title="查看种植建议资讯"
              >
                <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center shrink-0 group-hover:bg-green-100 transition-colors">
                  <ShieldCheckIcon className="h-4 w-4 text-green-700" aria-hidden />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-800 truncate max-w-[7rem] sm:max-w-none">
                      {crop.name}
                    </span>
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs font-medium border shrink-0 ${
                        riskColors[crop.risk] || riskColors['低风险']
                      }`}
                    >
                      {crop.risk}
                    </span>
                    {crop.sourceTags?.length > 0 ? (
                      <span className="text-xs text-gray-600 truncate max-w-full">
                        {crop.sourceTags.join('·')}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2 sm:truncate">
                    {crop.advice}
                  </p>
                  {crop.validUntil ? (
                    <p className="text-xs text-gray-600 mt-1 truncate">
                      {String(crop.validUntil).startsWith('有效')
                        ? crop.validUntil
                        : `有效至 ${crop.validUntil}`}
                      {crop.growthStage ? ` · ${crop.growthStage}` : ''}
                    </p>
                  ) : null}
                </div>
              </button>
              <div className="flex flex-col sm:flex-row items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={(e) => goInquiry(crop, e)}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-green-700"
                  title="AI 植保问询"
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden />
                  <span className="hidden sm:inline">问询</span>
                </button>
                <button
                  type="button"
                  onClick={() => goAdviceList(crop)}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-green-700"
                  title="种植建议资讯"
                >
                  <NewspaperIcon className="h-4 w-4" aria-hidden />
                  <span className="hidden sm:inline">资讯</span>
                </button>
                <ChevronRightIcon
                  className="hidden sm:block w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors"
                  aria-hidden
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PlantProtectionAdvice;
